import { supabase } from './supabase'

export const ACTIVE_ORG_STORAGE_KEY = 'naturassist_active_org_id'

/**
 * Get current user's organizations
 */
export async function getUserOrganizations() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return []

  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      *,
      organization:organizations(*)
    `)
    .eq('user_id', user.id)

  if (error) throw error
  return data || []
}

/**
 * Resolve active org id from storage and validate membership.
 */
export async function resolveActiveOrganizationId() {
  const orgs = await getUserOrganizations()
  if (orgs.length === 0) return null

  const memberOrgIds = orgs.map((row) => row.organization?.id).filter(Boolean)
  const storedId = typeof window !== 'undefined'
    ? localStorage.getItem(ACTIVE_ORG_STORAGE_KEY)
    : null

  if (storedId && memberOrgIds.includes(storedId)) {
    return storedId
  }

  const fallbackId = memberOrgIds[0]
  if (fallbackId && typeof window !== 'undefined') {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, fallbackId)
  }
  return fallbackId
}

/**
 * Get current user's active organization (persisted + membership validated)
 */
export async function getActiveOrganization() {
  const orgs = await getUserOrganizations()
  if (orgs.length === 0) return null

  const activeId = await resolveActiveOrganizationId()
  const match = orgs.find((row) => row.organization?.id === activeId)
  return match?.organization ?? orgs[0].organization
}

/**
 * Switch active organization after validating membership.
 */
export async function setActiveOrganization(orgId) {
  if (!orgId) {
    throw new Error('Organization id is required')
  }

  const role = await getUserRole(orgId)
  if (!role) {
    throw new Error('You are not a member of this organization')
  }

  if (typeof window !== 'undefined') {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId)
  }

  const orgs = await getUserOrganizations()
  const match = orgs.find((row) => row.organization?.id === orgId)
  return match?.organization ?? null
}

/**
 * Get current user's role in an organization
 */
export async function getUserRole(organizationId) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data, error } = await supabase
    .from('organization_members')
    .select('role, permissions')
    .eq('organization_id', organizationId)
    .eq('user_id', user.id)
    .single()

  if (error || !data) return null
  return data
}

/**
 * Check if user has permission for a resource
 */
export async function hasPermission(organizationId, resource, action = 'read') {
  const roleData = await getUserRole(organizationId)
  if (!roleData) return false

  const { role, permissions } = roleData

  // Admins have all permissions
  if (role === 'admin') return true

  // Check explicit permissions
  const resourcePerms = permissions?.[resource]
  if (resourcePerms === 'write' || resourcePerms === 'read') {
    if (action === 'read') return true
    if (action === 'write' && resourcePerms === 'write') return true
  }

  // Role-based defaults
  if (role === 'practitioner') {
    return action === 'read' || action === 'write'
  }
  if (role === 'viewer') {
    return action === 'read'
  }

  return false
}

/**
 * Get organization members
 */
export async function getOrganizationMembers(organizationId) {
  // Cannot join auth.users directly from public schema
  // Fetch members and then get user emails using batch function
  const { data, error } = await supabase
    .from('organization_members')
    .select('*')
    .eq('organization_id', organizationId)
    .order('joined_at', { ascending: false })

  if (error) throw error
  
  // Try to use batch function to get all emails and names at once
  let emailMap = new Map()
  let nameMap = new Map()
  try {
    const { data: emailData, error: emailError } = await supabase.rpc('get_org_member_emails', {
      org_id: organizationId
    })
    
    if (!emailError && emailData) {
      emailData.forEach(item => {
        emailMap.set(item.user_id, item.email)
        if (item.name) {
          nameMap.set(item.user_id, item.name)
        }
      })
    } else if (emailError) {
      console.warn('get_org_member_emails function not available, falling back to individual calls:', emailError)
      // Fall back to individual calls if batch function doesn't exist
      const emailPromises = (data || []).map(async (member) => {
        try {
          const { data: emailData, error: emailError } = await supabase.rpc('get_user_email', {
            user_uuid: member.user_id
          })
          if (!emailError && emailData) {
            return { user_id: member.user_id, email: emailData }
          }
        } catch (err) {
          console.error(`Error fetching email for user ${member.user_id}:`, err)
        }
        return null
      })
      
      const emailResults = await Promise.all(emailPromises)
      emailResults.forEach(result => {
        if (result) {
          emailMap.set(result.user_id, result.email)
        }
      })
    }
  } catch (err) {
    console.warn('Error using batch email function, falling back to individual calls:', err)
    // Fall back to individual calls
    const emailPromises = (data || []).map(async (member) => {
      try {
        const { data: emailData, error: emailError } = await supabase.rpc('get_user_email', {
          user_uuid: member.user_id
        })
        if (!emailError && emailData) {
          return { user_id: member.user_id, email: emailData }
        }
      } catch (err) {
        console.error(`Error fetching email for user ${member.user_id}:`, err)
      }
      return null
    })
    
    const emailResults = await Promise.all(emailPromises)
    emailResults.forEach(result => {
      if (result) {
        emailMap.set(result.user_id, result.email)
      }
    })
  }
  
  // Also check current user for their own email
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (user && user.id) {
      emailMap.set(user.id, user.email || null)
    }
  } catch (authErr) {
    // Ignore auth errors
  }
  
  // Map members with their emails and names
  const membersWithEmails = (data || []).map(member => ({
    ...member,
    user: {
      email: emailMap.get(member.user_id) || null,
      name: nameMap.get(member.user_id) || null
    }
  }))
  
  return membersWithEmails
}

/**
 * Invite user to organization
 * Enforces user and admin limits based on tier
 */
export async function inviteUserToOrganization(organizationId, email, role = 'member', permissions = {}) {
  // Check user limit first
  const { data: userLimitCheck, error: userLimitError } = await supabase.rpc('can_add_user', {
    org_id: organizationId
  })

  if (userLimitError) throw userLimitError
  if (!userLimitCheck?.allowed) {
    throw new Error(userLimitCheck?.reason || 'Cannot add more users. User limit reached.')
  }

  // If promoting to admin, check admin limit
  if (role === 'admin') {
    const { data: adminLimitCheck, error: adminLimitError } = await supabase.rpc('can_add_admin', {
      org_id: organizationId
    })

    if (adminLimitError) throw adminLimitError
    if (!adminLimitCheck?.allowed) {
      throw new Error(adminLimitCheck?.reason || 'Cannot add more admins. Admin limit reached.')
    }
  }

  // In a real app, you'd send an email invitation
  // For now, we'll just create the member record if the user exists
  const { data: { user } } = await supabase.auth.getUser()
  
  // Check if user exists
  const { data: existingUser } = await supabase.auth.admin.listUsers()
  const targetUser = existingUser?.users?.find(u => u.email === email)
  
  if (!targetUser) {
    throw new Error('User not found. Email invitation system needed.')
  }

  const { data, error } = await supabase
    .from('organization_members')
    .insert({
      organization_id: organizationId,
      user_id: targetUser.id,
      role,
      permissions,
      invited_by: user.id,
    })
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Update member role/permissions
 * Enforces admin limits when promoting to admin
 * Ensures minimum one admin requirement
 * Prevents non-master-admin from changing last admin
 * Prevents users from changing their own role
 */
export async function updateMember(organizationId, memberId, updates, isMasterAdmin = false) {
  // Get current user
  const { data: { user: currentUser } } = await supabase.auth.getUser()
  if (!currentUser) throw new Error('User not authenticated')

  // Get current member details
  const { data: currentMember, error: memberError } = await supabase
    .from('organization_members')
    .select('role, user_id')
    .eq('id', memberId)
    .single()

  if (memberError) throw memberError
  if (!currentMember) throw new Error('Member not found')

  // Prevent users from changing their own role (unless master admin)
  if (!isMasterAdmin && currentMember.user_id === currentUser.id) {
    throw new Error('You cannot change your own role. Please ask another admin to make this change.')
  }

  const currentRole = currentMember.role
  const newRole = updates.role

  // If changing from admin to non-admin, check minimum admin requirement
  if (currentRole === 'admin' && newRole && newRole !== 'admin') {
    // Get admin count
    const { data: adminCount, error: countError } = await supabase.rpc('get_admin_count', {
      org_id: organizationId
    })

    if (countError) throw countError

    // If this is the only admin, only master admin can change their role
    if (adminCount === 1) {
      if (!isMasterAdmin) {
        throw new Error('Cannot change the role of the last admin. Only master administrators can make this change.')
      }
    } else if (adminCount < 1) {
      throw new Error('Organization must have at least one admin')
    }
  }

  // If promoting to admin, check admin limit
  if (newRole === 'admin' && currentRole !== 'admin') {
    const { data: adminLimitCheck, error: adminLimitError } = await supabase.rpc('can_add_admin', {
      org_id: organizationId
    })

    if (adminLimitError) throw adminLimitError
    if (!adminLimitCheck?.allowed) {
      throw new Error(adminLimitCheck?.reason || 'Cannot add more admins. Admin limit reached.')
    }
  }

  const { data, error } = await supabase
    .from('organization_members')
    .update(updates)
    .eq('id', memberId)
    .eq('organization_id', organizationId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Remove member from organization
 * Prevents users from removing themselves
 */
export async function removeMember(organizationId, memberId) {
  // Get current user
  const { data: { user: currentUser } } = await supabase.auth.getUser()
  if (!currentUser) throw new Error('User not authenticated')

  // Get member details to check if it's the current user
  const { data: member, error: memberError } = await supabase
    .from('organization_members')
    .select('user_id')
    .eq('id', memberId)
    .eq('organization_id', organizationId)
    .single()

  if (memberError) throw memberError
  if (!member) throw new Error('Member not found')

  // Prevent users from removing themselves
  if (member.user_id === currentUser.id) {
    throw new Error('You cannot remove yourself from the organization. Please ask another admin to remove you.')
  }

  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', memberId)
    .eq('organization_id', organizationId)

  if (error) throw error
}

/**
 * Update organization settings
 */
export async function updateOrganization(organizationId, updates) {
  const { data, error } = await supabase
    .from('organizations')
    .update(updates)
    .eq('id', organizationId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Get subscription info
 * Tries organization_subscriptions table first, falls back to old subscriptions table
 */
export async function getSubscription(organizationId) {
  try {
    // Try new organization_subscriptions table first
    const { data: newSub, error: newError } = await supabase
      .from('organization_subscriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (!newError && newSub) {
      // Convert to format expected by components
      return {
        ...newSub,
        tier: newSub.tier_id ? null : null, // Will be resolved from organizations table
        status: newSub.status || 'active',
        starts_at: newSub.current_period_start,
        ends_at: newSub.current_period_end,
        billing_cycle: newSub.billing_cycle || 'monthly',
        current_period_start: newSub.current_period_start,
        current_period_end: newSub.current_period_end,
      }
    }

    // Fallback to old subscriptions table
    const { data: oldSub, error: oldError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (oldError && oldError.code !== 'PGRST116') {
      throw oldError
    }

    if (oldSub) {
      // Add default billing cycle for old subscriptions
      return {
        ...oldSub,
        billing_cycle: 'monthly',
        current_period_start: oldSub.starts_at,
        current_period_end: oldSub.ends_at,
      }
    }

    return null
  } catch (error) {
    console.error('Error getting subscription:', error)
    throw error
  }
}

